import React, { ReactElement, useEffect, useState } from 'react';
import { SetField } from './SetField';
import { NamedEntity } from '../../../types/entities/NamedEntity';
import { FieldComponent } from '../../../types/FieldComponent';
import { Status } from '../../../types/entities/Status';
import { compareText } from '../comparators/compareText';
import { SchemaWithClient } from '../../../types/SchemaWithClient';
import {
  Autocomplete,
  Button,
  Chip,
  TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import ColorizeIcon from '@mui/icons-material/Colorize';
import { SlidingPaneAddNew } from '../../Page/SlidingPaneAddNew';
import AddIcon from '@mui/icons-material/Add';
import LargePopper from '../assets/LargePopper';

type Props<T extends NamedEntity> = {
  onChange: (value: T) => void;
  schema: any;
  defaultValue: any;
  forcedValue: any;
  disabled: boolean;
  isMissing: boolean;
  accordingToValue: any;
  title?: string;
  allValue?: any;
  values?: any;
};

export const SPSelectSalesAgentField = <T extends NamedEntity>({
  onChange,
  defaultValue,
  forcedValue,
  disabled,
  isMissing,
  schema,
  accordingToValue,
                                                                 allValue,
                                                                 values,
  title,
}: Props<T>): ReactElement | null => {
  const [addingEntity, setAddingEntity] = useState(false);
  const [selectOptions, setSelectOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const [currentState, setCurrentState] = useState<any>({
    value: null,
    send: false,
  });

  useEffect(() => {
    if (
      typeof defaultValue !== undefined &&
      defaultValue !== null &&
      defaultValue !== undefined
    ) {
      setSelectOptions([defaultValue]);
    }
  }, []);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setCurrentState({
        value: defaultValue,
        send: false,
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (currentState.send) {
      onChange(currentState.value);
    }
  }, [currentState]);

  const fetchSelectOptions = async (searchText: string) => {
    setLoading(true);

    let string: any = {
      searchValue: searchText,
    };

    string.accordingToLabel = 'sales_partner';
    string.accordingToId = allValue?.sales_partner?.id;

    const searchParams = new URLSearchParams(string);

    schema.client.readNames(searchParams).then((result: any) => {
      if (result?.data) {
        result.data.unshift({ id: 0, name: '', isButton: true });
        setSelectOptions(result.data);
      } else {
        result.unshift({ id: 0, name: '', isButton: true });
        setSelectOptions(result);
      }
      setLoading(false);
    });
  };

  const handleOnChange = (event: any, value: any) => {
    if (value?.isButton) {
      setAddingEntity(true);
    } else {
      setCurrentState({
        value: value,
        send: true,
      });
    }
  };

  const [searchText, setSearchText] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);

  useEffect(() => {
    if (searchText === '') {
      setFilteredOptions(selectOptions); // Ha nincs keresés, minden opció látható
    } else {
      setFilteredOptions(
        selectOptions.filter((option: any) => {
          return option?.name
            ? option.name.toLowerCase().includes(searchText.toLowerCase())
            : false;
        })
      );
    }
  }, [searchText, selectOptions]);

  return (
    <>
      <Autocomplete
        freeSolo
        fullWidth
        onOpen={() => fetchSelectOptions('')}
        PopperComponent={LargePopper}
        size="small"
        options={filteredOptions}
        getOptionKey={(option: any) => option?.id}
        inputValue={searchText}
        renderOption={(props, option) => (
          <Box
            component="li"
            sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
            {...props}
          >
            {option?.isButton ? (
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="contained"
                fullWidth
              >
                Add new
              </Button>
            ) : option.color && typeof option.color === 'string' ? (
              <Chip
                style={{
                  background: `#${option.color}`,
                  color: '#fff',
                }}
                icon={<ColorizeIcon color="disabled" sx={{ color: 'white' }} />}
                label={`#${option.name}`}
              />
            ) : (
              option.name
            )}
          </Box>
        )}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={currentState.value}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={title}
            InputLabelProps={{
              style: {
                fontSize: 13,
                paddingTop: 2,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: <>{params.InputProps.endAdornment}</>,
            }}
          />
        )}
        onChange={(event, newValue) => {
          handleOnChange(event, newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setSearchText(newInputValue); // Frissítjük a keresési szöveget
        }}
        disabled={disabled}
      />
      {addingEntity && (
        <SlidingPaneAddNew
          schema={schema}
          mainSchema={schema}
          id={'new'}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            if (value) {
              setCurrentState({
                value: value,
                send: true,
              });
              setAddingEntity(false);
            }
          }}
          propertiesFromTable={{}}
          row={{}}
          onClose={(value) => {
            setAddingEntity(false);
          }}
        />
      )}
    </>
  );
};

SPSelectSalesAgentField.tableAdapter = <T extends NamedEntity = NamedEntity>(
  schema: SchemaWithClient<T>
) => {
  const Field: FieldComponent<T, T[]> = ({
    onChange,
    isActive: active,
    defaultValue,
    forcedValue,
    disabled,
    isMissing,
    accordingToValue,
    title,
    allValue,
  }) => {
    return (
      <SPSelectSalesAgentField
        onChange={onChange}
        defaultValue={defaultValue}
        forcedValue={forcedValue}
        disabled={!!disabled}
        isMissing={!!isMissing}
        schema={schema}
        accordingToValue={accordingToValue}
        title={title}
        allValue={allValue}
      />
    );
  };

  Field.includes = (query = [], value) =>
    value
      ? query.length === 0 || query.some((item) => value.id === item.id)
      : false;
  Field.comparator = (one, other) => compareText(one?.name, other?.name);
  Field.FilterRenderer = SetField.tableAdapter(schema).FilterRenderer;

  return Field;
};

function isStatusValue(value: NamedEntity): value is Status {
  if (typeof value === 'string' || value === null) {
    return false;
  }
  return 'color' in value;
}

function isStatusLamp(value: NamedEntity): value is Status {
  return 'status_lamp' in value;
}
